import { observable, reaction, computed, action, makeObservable } from 'decorators';
import moment from 'moment';
import api from 'services/api';
import auth from 'services/auth';
import log from 'services/log';

export default class TimePickStepStore {
  @observable isLoading = true;
  @observable isLoadingTimes = true;

  @observable timezone;
  @observable month;
  @observable selectedDay;
  @observable focusedDay;
  @observable times = [];

  @observable _showWeekends = true;
  @observable _start;
  @observable _end;
  @observable _scheduleData;
  @observable _min;
  @observable _max;

  constructor(parent) {
    makeObservable(this);
    this._parent = parent;
    this._changeDispose = reaction(() => this._dayOptions, () => this._refreshDayData());
    this._monthDispose = reaction(() => this.timezone, () => this._resetMonth());
    this._datesDispose = reaction(() => ({ m: this.month, s: this._showWeekends }), () => this._syncDates());
    this._timesDispose = reaction(() => this._timeOptions, () => this._refreshTimeData());
    this._currentDayLoad = 0;
    this._currentTimeLoad = 0;
    this.noFocusFirstLoad = false;

    // Pre load the timezone and let the reactions fire if internal
    if (this.internalMode) {
      setTimeout(action(() => { this.timezone = auth.userTimezone; }), 0);
    }
  }

  @computed get settings() {
    return this._parent.settings;
  }

  @computed get internalMode() {
    return this._parent.internalMode;
  }

  @computed get canPrevious() {
    return this.month && (!this._min || this.month.isAfter(this._min));
  }

  @computed get canNext() {
    return this.month && (!this._max || this.month.clone().endOf('month').isBefore(this._max));
  }

  @computed get headers() {
    return this._showWeekends ? [ 'Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat' ] : [ 'Mon', 'Tue', 'Wed', 'Thu', 'Fri' ];
  }

  @computed get hasNoCalendarData() {
    return !this.isLoading && (!this._scheduleData || !Object.keys(this._scheduleData).length);
  }

  @computed get calendarData() {
    const { _scheduleData: data, _start: start, _end: end, _showWeekends: wk, month } = this;
    if (!start || !end) { return []; }

    const calData = [];
    const day = start.clone();
    const monthNo = month.month();
    let isFirst = true;
    let week = 1;
    if (!wk) { day.add(1, 'd'); }
    while (day.isBefore(end)) {
      // Do a week of data at once
      const dayData = [];
      for (let i = 0; i < (wk ? 7 : 5); i++) {
        const dayD = data && data[day.format('YYYY-M-D')];
        const firstFocus = isFirst && !!dayD;
        if (firstFocus) { isFirst = false; }
        dayData.push({
          date: day.clone(),
          isCurrentMonth: day.month() === monthNo,
          data: dayD,
          firstFocus
        });
        day.add(1, 'd');
      }

      calData.push({ week, dayData });

      if (!wk) { day.add(2, 'd'); }
      week++;
    }
    return calData;
  }

  @computed get _dayOptions() {
    if (!this._start || !this._end) { return null; }

    const p = this._parent;
    if (!p.selectedType || !p.selectedLocation || (!p.settings.hideClinicians && !p.selectedClinician)) { return null; }

    const clinician = p.selectedClinician?.id;
    return {
      start: this._start.toISOString(),
      end: this._end.toISOString(),
      type: p.selectedType.id,
      location: p.selectedLocation.id,
      clinician,
      clinicianFilter: !clinician && p._cliniciansFilter?.length ? p._cliniciansFilter : null,
      options: this._parent._opts,
      filters: this._parent._lastResetSet
    };
  }

  @computed get _timeOptions() {
    if (!this.selectedDay) { return null; }

    const p = this._parent;
    if (!p.selectedType || !p.selectedLocation || (!p.settings.hideClinicians && !p.selectedClinician)) { return null; }

    const clinician = p.selectedClinician?.id;
    return {
      day: this.selectedDay.date.format('YYYY-MM-DD'),
      type: p.selectedType.id,
      location: p.selectedLocation.id,
      clinician,
      clinicianFilter: !clinician && p._cliniciansFilter?.length ? p._cliniciansFilter : null,
      options: this._parent._opts,
      filters: this._parent._lastResetSet
    };
  }

  @action moveMonth(amount) {
    this.selectedDay = null;
    this.month = this.month.clone().add(amount, 'M');
  }

  @action startFocus() {
    this.focusedDay = this.selectedDay?.date?.clone()
    || this.calendarData
      .find(w => w.dayData.find(d => d.firstFocus))
      ?.dayData.find(d => d.firstFocus)
      ?.date?.clone();
  }

  @action lostFocus() {
    this.focusedDay = null;
  }

  @action keyPress(e) {
    // Assume we are focused
    if (!this.focusedDay) { return; }

    // Find the position we want
    let firstOn = moment(this.focusedDay);
    let reverse = false;
    switch (e.key) {
      case 'Escape':
        // Special case, this will automatically unselect selected option
        if (this.selectedDay) {
          this.selectedDay = null;
          e.preventDefault();
          e.stopPropagation();
        }
        firstOn = null;
        break;
      case 'ArrowRight':
        firstOn.add(1, 'day');
        break;
      case 'ArrowLeft':
        firstOn.add(-1, 'day');
        reverse = true;
        break;
      case 'ArrowDown':
        firstOn.add(7, 'day');
        break;
      case 'ArrowUp':
        firstOn.add(-7, 'day');
        reverse = true;
        break;
      case 'Home':
        firstOn.startOf('week');
        break;
      case 'End':
        firstOn.endOf('week');
        reverse = true;
        break;
      case 'PageDown':
        firstOn.add(2, 'month'); // Go right off the 'edge'
        break;
      case 'PageUp':
        firstOn.add(-2, 'month'); // Go right off the 'edge'
        reverse = true;
        break;
      default:
        firstOn = null;
    }

    if (firstOn == null) { return; }
    e.preventDefault();
    e.stopPropagation();

    // Try and find the new pos
    const wkLength = this.calendarData.length;
    for (let w = (reverse ? wkLength - 1 : 0); (reverse ? w >= 0 : w < wkLength); (reverse ? w-- : w++)) {
      const dayLength = this.calendarData[w].dayData.length;
      for (let d = (reverse ? dayLength - 1 : 0); (reverse ? d >= 0 : d < dayLength); (reverse ? d-- : d++)) {
        const dayData = this.calendarData[w].dayData[d];
        if (!dayData.data) { continue; }
        if (reverse ? dayData.date.isSameOrBefore(firstOn) : dayData.date.isSameOrAfter(firstOn)) {
          // We found the date to travel to!
          this.focusedDay = dayData.date.clone();
          return;
        }
      }
    }

    // We didn't find anything, means we effectively went over the calendar bounds
    if (reverse && this.canPrevious) {
      this.moveMonth(-1);
    }
    if (!reverse && this.canNext) {
      this.moveMonth(1);
    }
  }

  @action selectDay(day) {
    if (day == null || (this.selectedDay && this.selectedDay.date.isSame(day.date))) {
      this.selectedDay = null;
    } else {
      this.selectedDay = day;

      if (this.month.month() !== day.date.month()) {
        this.month = day.date.clone().startOf('month');
      }
    }
  }

  selectTime(time, groupId) {
    this._parent.selectTime(moment(time).toISOString(), this.timezone, groupId);
  }

  @action _refreshDayData() {
    const opts = this._dayOptions;
    this.isLoading = true;
    const currentLoad = ++this._currentDayLoad;
    if (!opts) {
      this._scheduleData = null;
      return;
    }

    opts.timezone = this.timezone;
    api.post('appointment/scheduling/online/rangeAvailability', opts)
      .then(action(({ data }) => {
        if (currentLoad !== this._currentDayLoad) { return; }

        const { dayData, showWeekends, minimum, maximum } = data;
        this._scheduleData = dayData;
        this._showWeekends = showWeekends;
        this._min = minimum ? moment.tz(minimum, opts.timezone) : null;
        this._max = maximum ? moment.tz(maximum, opts.timezone) : null;
        const hasChange = this._checkMinMax();
        this.isLoading = false;
        if (!hasChange) {
          if (!this.noFocusFirstLoad) {
            this.startFocus();
          }
          this.noFocusFirstLoad = false;
        }
        return null;
      }))
      .catch(action(err => {
        log.error('Error getting availability days', err);
        this._parent.hadError = true;
      }))
      .done();
  }

  @action _refreshTimeData() {
    const opts = this._timeOptions;
    this.isLoadingTimes = true;
    const currentLoad = ++this._currentTimeLoad;
    if (!opts) {
      return;
    }

    opts.timezone = this.timezone;
    api.post('appointment/scheduling/online/dayAvailability', opts)
      .then(action(res => {
        if (currentLoad !== this._currentTimeLoad) { return; }

        this.times = res.data;
        this.isLoadingTimes = false;
        return null;
      }))
      .catch(action(err => {
        log.error('Error getting availability times', err);
        this._parent.hadError = true;
      }))
      .done();
  }

  @action _resetMonth() {
    const tz = this.timezone;
    if (!tz) { return; }

    this.selectedDay = null;
    const newMonth = this.month ? moment.tz([ this.month.year(), this.month.month(), 1 ], tz) : moment.tz(tz).startOf('month');

    // Don't need to worry about checking sync, since changing the timezone will always change the month
    this.month = newMonth;
    this._checkMinMax();
  }

  @action _syncDates() {
    const month = this.month;
    if (!month) { return; }

    const workingDay = month.clone().subtract(month.day() % 7, 'd');

    // Don't need to sync again if we are already matching - to prevent unneed refreshes
    if (this._start?.isSame(workingDay)) { return; }

    const startDay = workingDay.clone();
    const end = month.clone().endOf('month');

    while (workingDay.isSameOrBefore(end)) { workingDay.add(1, 'w'); }

    this._start = startDay;
    this._end = workingDay;
  }

  @action _checkMinMax() {
    if (!this.month) { return false; }

    let hasChange = false;
    if (this._min && this.month.clone().add(1, 'M').isBefore(this._min)) {
      this.month = this._min.clone().startOf('month');
      hasChange = true;
    }

    if (this._max && this.month.clone().subtract(1, 'M').isAfter(this._max)) {
      this.month = this._max.clone().startOf('month');
      hasChange = true;
    }

    return hasChange;
  }

  dispose() {
    this._changeDispose();
    this._monthDispose();
    this._datesDispose();
    this._timesDispose();
  }
}